import React, { useCallback, useEffect, useRef, useState } from "react";
import { Crisp } from "crisp-sdk-web"
import { useLocation, useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { Star, CheckCircle2 } from "lucide-react";
import Info from '../../components/Info';
import PlansSkeleton from "../../components/skeletons/pages/PlansSkeleton";
import { fetchIpInfoService } from "../../utils/Country";
import * as Sentry from '@sentry/react';
import PLANS from "../../constants/plans.json";
import { useOrganisation } from "../../http-hooks/organisation/useOrganisation";
import { useBilling } from "../../hooks/useBilling";
import { addMixPanelEvent } from "../../store/actions";
import { getPlanTier } from "../../components/plans/PlanCardsGrid";

const plans = PLANS.filter(item => item.is_active).slice(1);

export default function Plans() {

	const { search } = useLocation();
	const queryString = new URLSearchParams(search)
	const plan_id = queryString.get("plan_id")
	const { org_id } = useParams();
	const history = useHistory();
	const dispatch = useDispatch();
	const { startCheckout, createNewHostedPageFn } = useBilling({ orgId: org_id });
	
	// Use React Query hook for organisation data
	const { isLoading: isOrgLoading, isError, data: org } = useOrganisation({ org_id });
	
	const [isAnnualBillingCycle, setIsAnnualBillingCycle] = useState(false);
	const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
	const [country, setCountry] = useState(null);
	const hasAutoOpenedRef = useRef(false);

	const isStarterPlan = (plan) =>
		plan.plan_id_usd === "starter-plan-usd-unlimited" ||
		plan.plan_id_inr === "starter-plan-inr-unlimited";

	const openCheckout = useCallback(async (plan_id, source) => {
		setIsCheckoutLoading(true);
		try {
			const url = window.location.origin + `/organisation/${org_id}/integration`;
			const hostedPageFn = createNewHostedPageFn({
				planId: plan_id,
				redirectURL: url,
				cancelURL: url,
				source,
			});

			await startCheckout({
				hostedPageFn,
				afterValidate: async () => {
					history.push(`/organisation/${org_id}/integration`);
				},
				onClose: function () {
					// user closed popup; stay on page
				},
				planId: plan_id,
			});
		} catch (err) {
			// errors already toasted + notified in useBilling
		} finally {
			setIsCheckoutLoading(false);
		}

	}, [org_id, history, startCheckout, createNewHostedPageFn])

	const onSubscribe = useCallback((plan, country, source = "new_subscriber") => {

		const selectedPlanId = "IN" === country
			? (isAnnualBillingCycle ? plan.plan_id_inr_annual : plan.plan_id_inr)
			: (isAnnualBillingCycle ? plan.plan_id_usd_annual : plan.plan_id_usd);

		dispatch(addMixPanelEvent({
			orgId: org_id, body: {
				eventName: "SELECT_PLAN",
				eventData: {
					plan_id: selectedPlanId,
					plan_tier: getPlanTier(selectedPlanId),
					source,
				}
			}
		}));

		openCheckout(selectedPlanId, source);
	}, [isAnnualBillingCycle, openCheckout, dispatch, org_id]);

	const autoOpenCheckout = useCallback(async () => {

		setIsCheckoutLoading(true);

		try {
			const ipInfo = await fetchIpInfoService();
			const fetchedCountry = ipInfo.country;
			setCountry(fetchedCountry);

			if (fetchedCountry && plan_id) {
				const plan = plans.find(
					row => (row.plan_id_usd === plan_id) || (row.plan_id_inr === plan_id)
				)
				if (plan) {
					setTimeout(() => {
						onSubscribe(plan, fetchedCountry, "deep_link")
					}, 0)
				}
			}

		} catch (err) {
			console.log("checkouterror", err)
			toast.error("Something went wrong!");
			Sentry.captureException(err);
		} finally {
			setIsCheckoutLoading(false);
		}

	}, [plan_id, onSubscribe])

	useEffect(() => {
		if (!isOrgLoading && !hasAutoOpenedRef.current) {
			hasAutoOpenedRef.current = true;
			autoOpenCheckout()
		}
	}, [autoOpenCheckout, isOrgLoading]);

	// Show error state
	if (isError) {
		return (
			<div className="container-fluid">
				<div className="container">
					<div className="my-5 text-center">
						<p className="text-danger">Failed to load organisation data. Please try again.</p>
					</div>
				</div>
			</div>
		);
	}

	// Show loading state
	const isLoading = isOrgLoading || isCheckoutLoading;

	return (
		<>
			<div className="container-fluid">
				<div className="container">
					<div className="row my-5">
						<div className="col-12 mb-4">
							<h2 className="text-center fw-bold mb-2">Choose Your Plan</h2>
							<p className="text-center text-muted mb-4">Select the perfect plan for your needs</p>
						</div>
						<div className="col-12">
							<div className="d-flex align-items-center justify-content-center gap-3">
								<span className={`fw-semibold ${!isAnnualBillingCycle ? 'text-primary' : 'text-muted'}`}>
									Monthly
								</span>
								<div className="form-check form-switch mb-0">
									<input 
										onChange={(e) => setIsAnnualBillingCycle(e.target.checked)} 
										type="checkbox" 
										className="form-check-input" 
										style={{ cursor: 'pointer', width: '3rem', height: '1.5rem' }}
										id="billing_cycle_toggle" 
										name="billing_cycle_toggle"
										checked={isAnnualBillingCycle}
									/>
								</div>
								<span className={`fw-semibold ${isAnnualBillingCycle ? 'text-primary' : 'text-muted'}`}>
									Annual
									<span className="badge bg-success ms-2 align-middle" style={{ fontSize: '0.7rem' }}>
										Save 1 month
									</span>
								</span>
							</div>
						</div>
					</div>
					{isLoading ? (
						<PlansSkeleton showToggle={false} />
					) : (
						<>
							<div className="row mt-4 justify-content-center g-3 align-items-stretch">
								{plans.map((plan) => {
									const isStarter = isStarterPlan(plan);
									const isCurrent = org && (
										org.plan_id === plan.plan_id_inr ||
										org.plan_id === plan.plan_id_inr_annual ||
										org.plan_id === plan.plan_id_usd ||
										org.plan_id === plan.plan_id_usd_annual
									);

									return (
										<div className="col-12 col-md-6 col-lg-4 col-xl-3" key={plan.plan_id_inr || plan.plan_title}>
											<div className={`h-100 ${isStarter ? 'pt-2' : ''}`}>
												<div
													className="card h-100 border-0 position-relative"
													style={isStarter ? {
														outline: '2.5px solid var(--bs-primary)',
														borderRadius: '14px',
														boxShadow: '0 8px 28px rgba(13,110,253,.18)',
													} : {
														borderRadius: '14px',
														boxShadow: '0 2px 10px rgba(0,0,0,.07)',
													}}
												>
													{isStarter && (
														<div
															className="position-absolute top-0 start-50 translate-middle d-flex align-items-center gap-1 bg-primary text-white rounded-pill px-3 py-1 shadow-sm"
															style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.04em', zIndex: 5, whiteSpace: 'nowrap' }}
														>
															<Star size={12} fill="currentColor" />
															Most Popular
														</div>
													)}

													<div className="card-body d-flex flex-column p-4" style={{ paddingTop: isStarter ? '2.2rem' : '1.5rem' }}>

														{/* Plan name */}
														<h6 className="fw-bold text-center mb-1 text-body-emphasis" style={{ fontSize: '1.1rem' }}>
															{plan.plan_title}
															{isCurrent && (
																<span className="badge bg-primary ms-2 align-middle" style={{ fontSize: '0.6rem' }}>
																	Current
																</span>
															)}
														</h6>
														<p className="text-center text-muted mb-4" style={{ fontSize: '0.8rem', minHeight: '32px' }}>
															{plan.plan_sub_title}
														</p>

													{/* Pricing */}
													<div className="text-center mb-3">
														<span className="fw-bold text-dark" style={{ fontSize: '2.4rem', lineHeight: 1 }}>
															{isAnnualBillingCycle ? `$${plan.annual_plan_monthly_price}` : plan.price}
														</span>
														<p className="text-muted mt-1 mb-0" style={{ fontSize: '0.8rem' }}>
															{isAnnualBillingCycle
																? <>${plan.annual_price} billed annually</>
																: 'per month'
															}
														</p>
													</div>

														{/* CTA */}
														<div className="mt-3 mb-4">
															<button
																onClick={() => onSubscribe(plan, country)}
																className={`btn w-100 fw-semibold ${isStarter ? 'btn-primary' : 'btn-outline-primary'}`}
															>
																{isCurrent ? 'Current Plan ✓' : isStarter ? 'Get Started' : 'Choose Plan'}
															</button>
														</div>

														{/* Feature list */}
														<hr className="mt-0 mb-3" />
														{plan.subtitle1 && (
															<p className="fw-semibold mb-2" style={{ fontSize: '0.78rem', color: 'var(--bs-secondary-color)' }}>
																{plan.subtitle1}
															</p>
														)}
														<ul className="list-unstyled mb-0 flex-grow-1">
															{plan.features.map((feature, featureIndex) => (
																<li key={`${plan.plan_id_inr}-feature-${featureIndex}`} className="d-flex align-items-start mb-2">
																	<CheckCircle2 size={15} className="text-success flex-shrink-0 me-2 mt-1" />
																	<span style={{ fontSize: '0.82rem', color: 'var(--bs-body-color)' }}>
																		{feature.text}{feature.help_text ? <> <Info text={feature.help_text} /></> : null}
																	</span>
																</li>
															))}
														</ul>
													</div>
												</div>
											</div>
										</div>
									);
								})}
							</div>
							<div className="row my-5 align-items-center justify-content-center">
								<div className="col-md-9 text-center">
									<span style={{ fontSize: "24px", color: "var(--bs-body-color)" }}>
										Do not fit into any of the plans? Chat with us
										<br /> and get an{" "}
										<span className="text-primary">Enterprise Plan</span>
									</span>
								</div>
								<div className="col-md-3">
									<button
										type="button"
										onClick={(e) => {
											Crisp.chat.open()
										}}
										className="btn btn-primary w-100 mt-3 mt-sm-0"
									>
										Talk to us
									</button>
								</div>
							</div>
						</>
					)}
				</div>
			</div>
		</>
	);
}
